import { useEffect, useRef, useState } from 'react';
import api from '../../api/client';

// Tira horizontal de programación, embebida en la fila del header
// (ONLINE · OYENTES · reloj · ESTA TIRA · Sonando).
// Izquierda: 12 h pasadas (atenuadas). Centro: lo que está sonando, en rojo,
// auto-centrado. Derecha: próximas 12 h.
// - Scroll con la rueda del mouse, sin scrollbar visible.
// - Al entrar al panel (o cuando cambia el programa) se centra el actual.
// - Cada 2 minutos vuelve al actual, aunque hayas scrolleado.
export default function ProgramStrip() {
  const [data, setData] = useState(null);
  const scrollRef = useRef(null);
  const currentRef = useRef(null);
  const centeredKeyRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get('/api/stats/schedule-now')
        .then(({ data: d }) => { if (alive) setData(d); })
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const seq = data?.sequence || [];
  const currentIndex = seq.findIndex((p) => p.is_current);
  const currentKey = data?.current?.program || null;

  const recenter = () => {
    const c = scrollRef.current;
    const cur = currentRef.current;
    if (c && cur) {
      c.scrollLeft = Math.max(0, cur.offsetLeft - c.clientWidth / 2 + cur.offsetWidth / 2);
    }
  };

  // Centrar el actual al cargar por primera vez o cuando cambia el programa
  useEffect(() => {
    if (seq.length && currentKey && centeredKeyRef.current !== currentKey) {
      centeredKeyRef.current = currentKey;
      requestAnimationFrame(recenter);
    }
  }, [currentKey, seq.length]);

  // Volver al programa actual cada 2 minutos (aunque hayas scrolleado)
  useEffect(() => {
    const t = setInterval(() => recenter(), 120000);
    return () => clearInterval(t);
  }, []);

  // Scroll horizontal con la rueda del mouse.
  // IMPORTANTE: este efecto depende de seq.length porque la tira no existe
  // hasta que llegan los datos; con [] el ref era null y nunca se enganchaba.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      } else {
        el.scrollLeft += e.deltaX;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [seq.length]);

  if (!seq.length) return null;

  return (
    <div
      className="relative min-w-0 flex-1"
      title="Programación: 12 h antes y después de ahora · girá la rueda del mouse para deslizar · vuelve solo al actual cada 2 min"
    >
      <div
        ref={scrollRef}
        className="flex items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {seq.map((p, i) => {
          const isCur = p.is_current;
          const passed = !isCur && currentIndex !== -1 && i < currentIndex;
          return (
            <div
              key={i}
              ref={isCur ? currentRef : undefined}
              title={`${p.program} · ${p.when} ${p.range}`}
              className={`flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-1.5 ${
                isCur
                  ? 'w-52 border-error-600 bg-error-900/40 shadow-[0_0_8px_rgba(185,28,28,.45)]'
                  : passed
                    ? 'w-36 border-dark-800 bg-dark-850/50 opacity-60'
                    : 'w-36 border-dark-700 bg-dark-850'
              }`}
            >
              {p.image ? (
                <img
                  src={p.image}
                  alt=""
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="h-6 w-6 shrink-0 rounded border border-dark-600 object-cover"
                />
              ) : (
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border text-[10px] ${
                  isCur ? 'border-error-700/60 bg-error-900/40' : 'border-dark-700 bg-dark-800'
                }`}>🎙️</span>
              )}
              <span className="min-w-0 leading-tight">
                <span className={`block text-[8px] font-bold uppercase tracking-wide ${
                  isCur ? 'text-error-300' : 'text-dark-500'
                }`}>
                  {isCur ? `AHORA ${p.range}` : `${passed ? '✓ ' : ''}${p.range.split('–')[0]}`}
                </span>
                <b className={`block truncate text-[10px] ${isCur ? 'text-white' : 'text-dark-200'}`}>
                  {p.program}
                </b>
              </span>
            </div>
          );
        })}
      </div>
      {/* Desvanecidos laterales: insinúan que hay más contenido hacia cada lado */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-dark-850 to-transparent"></div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-dark-850 to-transparent"></div>
    </div>
  );
}
